import React from 'react';
import { AuthService, AuthResult } from '../Auth';

interface AuthContextValue {
    authResponse: AuthResult | null;
    loginError: string | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    apiBase: string;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [authResponse, setAuthResponse] = React.useState<AuthResult | null>(null);
    const [loginError, setLoginError] = React.useState<string | null>(null);

    const authService = React.useMemo(() => new AuthService(), []);

    const API_BASE = (process.env.REACT_APP_BACKEND_API_ENDPOINT ?? '').replace(/\/$/, '');
    const apiBase = API_BASE ? `${API_BASE}/api` : '/api';

    React.useEffect(() => {
        const stored = authService.getStoredAuth();
        if (stored) setAuthResponse(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = React.useCallback(async (email: string, password: string): Promise<void> => {
        setLoginError(null);
        try {
            const result = await authService.login(email, password, apiBase);
            setAuthResponse(result);
        } catch (e) {
            setLoginError(e instanceof Error ? e.message : 'Přihlášení selhalo.');
            throw e;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiBase]);

    const logout = React.useCallback((): void => {
        authService.logout();
        setAuthResponse(null);
        setLoginError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider value={{ authResponse, loginError, login, logout, apiBase }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = React.useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
